import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Navigate } from 'react-router-dom';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const nextStatusFor = { Packed: "Shipped", Shipped: "Out for Delivery", "Out for Delivery": "Delivered", "On Hold": "Out for Delivery" };

const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const DriverPortal = () => {
    const { enqueueSnackbar } = useSnackbar();
    const { isAuthenticated, user, loading: authLoading } = useSelector((state) => state.user);

    const [active, setActive] = useState([]); // not yet Delivered/Cancelled
    const [history, setHistory] = useState([]); // Delivered/Cancelled
    const [loading, setLoading] = useState(true);

    const [topTab, setTopTab] = useState('today'); // 'today' | 'past'
    const [subTab, setSubTab] = useState('pending'); // 'pending' | 'completed' | 'hold'

    const [holdingOrderId, setHoldingOrderId] = useState(null);
    const [holdReason, setHoldReason] = useState("");
    const [submittingHold, setSubmittingHold] = useState(false);

    const fetchAll = useCallback(async () => {
        try {
            const [activeRes, historyRes] = await Promise.all([
                axios.get('/api/v1/driver/my-deliveries'),
                axios.get('/api/v1/driver/my-deliveries/history'),
            ]);
            setActive(activeRes.data.orders);
            setHistory(historyRes.data.orders);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to load deliveries", { variant: "error" });
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    useEffect(() => {
        if (isAuthenticated && user?.role === 'driver') {
            fetchAll();
        }
    }, [isAuthenticated, user, fetchAll]);

    // The single active delivery is derived from real data — whichever
    // order (if any) is currently "Out for Delivery".
    const activeOrder = active.find((o) => o.orderStatus === "Out for Delivery");
    const activeOrderId = activeOrder?._id ?? null;

    useEffect(() => {
        if (!activeOrderId) return;

        const sendLocation = () => {
            if (!navigator.geolocation) return;
            navigator.geolocation.getCurrentPosition((pos) => {
                axios.post('/api/v1/driver/location', {
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude,
                    orderId: activeOrderId,
                }).catch(() => {});
            });
        };

        sendLocation();
        const interval = setInterval(sendLocation, 15000);
        return () => clearInterval(interval);
    }, [activeOrderId]);

    const advanceStatusHandler = async (order) => {
        const nextStatus = nextStatusFor[order.orderStatus];
        if (!nextStatus) return;

        const isCOD = order.paymentInfo?.status !== 'PAID';
        if (nextStatus === 'Delivered' && isCOD) {
            const confirmed = window.confirm(
                `Confirm you have collected HK$${order.totalPrice.toLocaleString()} in cash from the customer before marking this order Delivered.`
            );
            if (!confirmed) return;
        }

        try {
            await axios.put(`/api/v1/driver/delivery/${order._id}/status`, { status: nextStatus });
            enqueueSnackbar(`Marked as ${nextStatus}`, { variant: "success" });
            fetchAll();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to update status", { variant: "error" });
        }
    };

    const openHoldBox = (orderId) => {
        setHoldingOrderId(orderId);
        setHoldReason("");
    };

    const cancelHoldBox = () => {
        setHoldingOrderId(null);
        setHoldReason("");
    };

    const submitHold = async (orderId) => {
        if (!holdReason.trim()) {
            enqueueSnackbar("Please enter a reason for the hold", { variant: "warning" });
            return;
        }

        setSubmittingHold(true);
        try {
            await axios.put(`/api/v1/driver/delivery/${orderId}/hold`, { reason: holdReason.trim() });
            enqueueSnackbar("Delivery put on hold", { variant: "success" });
            cancelHoldBox();
            fetchAll();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to hold this delivery", { variant: "error" });
        } finally {
            setSubmittingHold(false);
        }
    };

    const directionsHref = (order) => {
        const address = `${order.shippingInfo.address}, ${order.shippingInfo.city}, ${order.shippingInfo.state}`;
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    };

    if (authLoading) return <BackdropLoader />;
    if (!isAuthenticated) return <Navigate to="/login?redirect=driver" />;
    if (user?.role !== 'driver') return <Navigate to="/" />;

    // --- Bucket into Today/Past and Pending/Completed/Hold ---
    const today = new Date();
    const pendingToday = active.filter((o) => o.orderStatus !== 'On Hold');
    const holdToday = active.filter((o) => o.orderStatus === 'On Hold');
    const completedToday = history.filter((o) => {
        const d = o.deliveredAt ? new Date(o.deliveredAt) : new Date(o.createdAt);
        return isSameDay(d, today);
    });
    const pastDeliveries = history.filter((o) => {
        const d = o.deliveredAt ? new Date(o.deliveredAt) : new Date(o.createdAt);
        return !isSameDay(d, today);
    });

    let displayedOrders;
    if (topTab === 'past') {
        displayedOrders = pastDeliveries;
    } else if (subTab === 'completed') {
        displayedOrders = completedToday;
    } else if (subTab === 'hold') {
        displayedOrders = holdToday;
    } else {
        displayedOrders = pendingToday;
    }

    return (
        <>
            <MetaData title="Driver Portal | 1313 Mart" />
            {loading && <BackdropLoader />}

            <main className="max-w-2xl mx-auto p-4 pt-20 sm:pt-8">
                <h1 className="text-xl font-medium mb-4">My Deliveries</h1>

                {/* Top tabs */}
                <div className="flex border-b mb-3">
                    <button
                        onClick={() => setTopTab('today')}
                        className={`flex-1 py-2 text-sm font-medium border-b-2 ${topTab === 'today' ? 'border-primary-orange text-primary-orange' : 'border-transparent text-gray-500'}`}
                    >
                        Today Deliveries
                    </button>
                    <button
                        onClick={() => setTopTab('past')}
                        className={`flex-1 py-2 text-sm font-medium border-b-2 ${topTab === 'past' ? 'border-primary-orange text-primary-orange' : 'border-transparent text-gray-500'}`}
                    >
                        Past Deliveries
                    </button>
                </div>

                {/* Sub tabs, only under Today */}
                {topTab === 'today' && (
                    <div className="flex gap-2 mb-4">
                        {[
                            { key: 'pending', label: `Pending (${pendingToday.length})` },
                            { key: 'completed', label: `Completed (${completedToday.length})` },
                            { key: 'hold', label: `Hold (${holdToday.length})` },
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setSubTab(tab.key)}
                                className={`flex-1 py-1.5 rounded-full text-xs font-medium ${subTab === tab.key ? 'bg-primary-green text-white' : 'bg-gray-100 text-gray-600'}`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                )}

                {!loading && displayedOrders.length === 0 && (
                    <p className="text-gray-500 text-sm">Nothing here right now.</p>
                )}

                <div className="flex flex-col gap-3">
                    {displayedOrders.map((order) => {
                        const nextStatus = nextStatusFor[order.orderStatus];
                        const blockedByAnotherActiveDelivery =
                            nextStatus === "Out for Delivery" && activeOrderId && activeOrderId !== order._id;
                        const isHoldingThis = holdingOrderId === order._id;
                        const isCOD = order.paymentInfo?.status !== 'PAID';
                        const isFinished = order.orderStatus === 'Delivered' || order.orderStatus === 'Cancelled';

                        return (
                            <div key={order._id} className="bg-white rounded-lg shadow p-4 flex flex-col gap-2">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <p className="font-medium">Order #{order.orderNumber ?? order._id}</p>
                                        <p className="text-sm text-gray-500">{order.orderItems.length} item(s) · HK${order.totalPrice.toLocaleString()}</p>
                                    </div>
                                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{order.orderStatus}</span>
                                </div>

                                <p className="text-sm text-gray-600">{order.shippingInfo.address}, {order.shippingInfo.city}, {order.shippingInfo.state}</p>
                                <p className="text-sm text-gray-600">Phone: {order.shippingInfo.phoneNo}</p>

                                <div className="flex items-center gap-3">
                                    <a
                                        href={directionsHref(order)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-primary-blue hover:underline w-fit"
                                    >
                                        📍 Get Directions
                                    </a>
                                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${isCOD ? 'bg-orange-50 text-primary-orange' : 'bg-green-50 text-primary-green'}`}>
                                        {isCOD ? 'Cash on Delivery' : 'Paid Online'}
                                    </span>
                                </div>

                                {order.holdReasons?.length > 0 && (
                                    <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-xs text-yellow-800">
                                        <p className="font-medium mb-1">Previous attempt(s):</p>
                                        {order.holdReasons.map((h, i) => (
                                            <p key={i}>&bull; {h.reason}</p>
                                        ))}
                                    </div>
                                )}

                                {!isFinished && nextStatus && !blockedByAnotherActiveDelivery && !isHoldingThis && (
                                    <button
                                        onClick={() => advanceStatusHandler(order)}
                                        className="mt-2 bg-primary-orange text-white text-sm font-medium py-2 rounded shadow hover:shadow-lg"
                                    >
                                        Mark as {nextStatus}
                                    </button>
                                )}
                                {blockedByAnotherActiveDelivery && (
                                    <p className="text-xs text-gray-400 mt-2">
                                        Finish your current delivery before starting this one.
                                    </p>
                                )}

                                {order.orderStatus === "Out for Delivery" && !isHoldingThis && (
                                    <>
                                        <p className="text-xs text-primary-green">Sharing your live location for this delivery...</p>
                                        <button
                                            onClick={() => openHoldBox(order._id)}
                                            className="text-xs text-red-500 hover:underline w-fit mt-1"
                                        >
                                            Couldn't deliver? Put on hold
                                        </button>
                                    </>
                                )}

                                {isHoldingThis && (
                                    <div className="flex flex-col gap-2 mt-2 border-t pt-2">
                                        <textarea
                                            value={holdReason}
                                            onChange={(e) => setHoldReason(e.target.value)}
                                            placeholder="e.g. Customer not home, will retry later"
                                            className="border rounded p-2 text-sm w-full"
                                            rows={2}
                                        />
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => submitHold(order._id)}
                                                disabled={submittingHold}
                                                className="flex-1 bg-red-500 text-white text-sm font-medium py-2 rounded shadow hover:shadow-lg disabled:opacity-60"
                                            >
                                                {submittingHold ? "Submitting..." : "Confirm Hold"}
                                            </button>
                                            <button
                                                onClick={cancelHoldBox}
                                                disabled={submittingHold}
                                                className="flex-1 bg-gray-100 text-gray-700 text-sm font-medium py-2 rounded"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </main>
        </>
    );
};

export default DriverPortal;